/* ==========================================================================
   GAUI — predictive-search.js
   Overlay search with live predictions via the Section Rendering API.
   Accessible combobox: input + listbox, arrow-key navigation, Escape to close.
   ========================================================================== */
(function () {
  "use strict";

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  if (!("customElements" in window) || customElements.get("predictive-search")) return;

  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector("[data-predictive-input]");
      this.live = this.querySelector("[data-predictive-live]");
      this.defaultGroup = this.querySelector("[data-predictive-default]");
      this.controller = null;
      this.lastFocused = null;
      this.activeIndex = -1;
      this.debounce = null;
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (this.input) {
        this.input.addEventListener("input", () => {
          clearTimeout(this.debounce);
          this.debounce = setTimeout(() => this.search(this.input.value.trim()), 220);
        });
      }
      this.addEventListener("click", (e) => {
        if (e.target.closest("[data-predictive-close]") || e.target.hasAttribute("data-predictive-overlay")) {
          this.close();
        }
      });
      this.assignIds();
    }

    // Give every option a stable id so aria-activedescendant resolves — covers
    // the server-rendered default popular-search chips, not just post-fetch results.
    assignIds() {
      Array.prototype.slice.call(this.querySelectorAll("[data-predictive-option]")).forEach(function (el, i) {
        el.id = "srch-opt-" + i;
      });
    }

    open() {
      this.lastFocused = document.activeElement;
      this.classList.add("is-open");
      document.body.classList.add("drawer-open");
      document.addEventListener("keydown", this.onKeydown);
      window.requestAnimationFrame(() => this.input && this.input.focus());
    }

    close() {
      this.classList.remove("is-open");
      document.body.classList.remove("drawer-open");
      document.removeEventListener("keydown", this.onKeydown);
      if (this.input) {
        this.input.setAttribute("aria-expanded", "false");
        this.input.removeAttribute("aria-activedescendant");
      }
      if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
    }

    showDefault() {
      if (this.live) this.live.innerHTML = "";
      if (this.defaultGroup) this.defaultGroup.hidden = false;
      if (this.input) {
        this.input.setAttribute("aria-expanded", "false");
        this.input.removeAttribute("aria-activedescendant");
      }
      this.activeIndex = -1;
    }

    search(term) {
      if (!term || term.length < 2) {
        this.showDefault();
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add("is-loading");

      var params =
        "q=" + encodeURIComponent(term) +
        "&section_id=predictive-search" +
        "&resources[type]=product,query" +
        "&resources[limit]=6&resources[limit_scope]=each";

      fetch(root() + "search/suggest?" + params, { signal: this.controller.signal })
        .then((r) => r.text())
        .then((text) => {
          var parsed = new DOMParser().parseFromString(text, "text/html");
          var fresh = parsed.querySelector("[data-predictive-live]");
          if (this.live && fresh) this.live.innerHTML = fresh.innerHTML;
          if (this.defaultGroup) this.defaultGroup.hidden = true;
          if (this.input) this.input.setAttribute("aria-expanded", "true");
          this.assignIds();
          this.activeIndex = -1;
        })
        .catch((err) => {
          if (err.name !== "AbortError") this.showDefault();
        })
        .finally(() => this.classList.remove("is-loading"));
    }

    options() {
      return Array.prototype.slice.call(this.querySelectorAll("[data-predictive-option]")).filter(function (el) {
        return el.offsetParent !== null;
      });
    }

    setActive(index) {
      var opts = this.options();
      if (!opts.length) return;
      this.activeIndex = (index + opts.length) % opts.length;
      opts.forEach(function (el, i) {
        var on = i === this.activeIndex;
        el.classList.toggle("is-active", on);
        el.setAttribute("aria-selected", on ? "true" : "false");
      }, this);
      if (this.input) this.input.setAttribute("aria-activedescendant", opts[this.activeIndex].id);
      opts[this.activeIndex].scrollIntoView({ block: "nearest" });
    }

    onKeydown(e) {
      if (e.key === "Escape") {
        this.close();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.setActive(this.activeIndex + 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.setActive(this.activeIndex - 1);
      } else if (e.key === "Enter") {
        var opts = this.options();
        if (this.activeIndex >= 0 && opts[this.activeIndex]) {
          e.preventDefault();
          opts[this.activeIndex].click();
        }
      }
    }
  }

  customElements.define("predictive-search", PredictiveSearch);

  // Open from any search entry point: header icon, 404/mobile links
  // ([data-search-toggle]) and the Shop mega-menu search field
  // ([data-search-open], which otherwise just focuses a dead input).
  document.addEventListener("click", function (e) {
    var toggle = e.target.closest("[data-search-toggle], [data-search-open]");
    if (!toggle) return;
    var panel = document.querySelector("predictive-search");
    if (!panel) return;
    e.preventDefault();
    panel.open();
  });
})();
